/**
 * Вкладки окна карточки площадки, общие для ВБ, ОЗОН, Маркета и Сайта:
 * Обзор · Текст · Характеристики · Цена и остатки · Склейка · История.
 * «Медиа» у площадок разное и собирается в `McardWindow`.
 */

import React, { useEffect, useState } from 'react'
import { AlertTriangle, ExternalLink, ImageOff, Layers, Search } from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { cn } from '../../../../lib/cn'
import type { EntityTarget } from '../../../../components/entity-view/navigatorContext'
import {
  cardChangeSendsAPI,
  marketplaceCardChangesAPI,
} from '../../../marketplace-card-changes/api'
import {
  formatPlainValue,
  SEND_OUTCOME_LABELS,
  STATUS_LABELS as CHANGE_STATUS_LABELS,
  sendOriginLabel,
} from '../../../marketplace-card-changes/presentation'
import type {
  CardChangeMarketplace,
  CardChangeSendSummary,
  ChangeSummary,
} from '../../../marketplace-card-changes/types'
import { marketplaceBundlesAPI } from '../../../marketplace-bundles/api'
import type { MarketplaceBundleCard } from '../../../marketplace-bundles/types'
import { PRESENCE_STATE_INFO } from '../../../products-v2/lib'
import { PRESENCE_CHAIN_LABEL, presenceTarget } from '../../../products-v2/presence'
import type { ProductPresenceEntry } from '../../../products-v2/types'
import {
  characteristicText,
  formatDimensions,
  formatRub,
  PLATFORM_NAME,
  type McardIssue,
  type McardModel,
  type McardPlatform,
} from './model'
import {
  ORIGIN_LABELS,
  effectiveValue,
  type CharacteristicRow,
} from './characteristics'
import { Notice, NoData, Panel, ReadonlyValue, Segmented, fieldClass, formatDateTime } from './McardParts'
import { DIMENSION_AXES, DIMENSION_AXIS_LABELS, type DimensionAxis, type McardEditApi } from './useMpCardEdit'
import type { CardWindowState } from './windowEditApi'

// ── Обзор ───────────────────────────────────────────────────────────────────

const IssuesBlock: React.FC<{ issues: McardIssue[] }> = ({ issues }) => {
  if (issues.length === 0) {
    return <Notice>Ошибок и замечаний площадки нет.</Notice>
  }
  const ordered = [...issues.filter((i) => i.level === 'error'), ...issues.filter((i) => i.level === 'warning')]
  return (
    <div className="space-y-2">
      {ordered.map((issue, index) => (
        <div
          key={`${issue.title}-${index}`}
          className={
            issue.level === 'error'
              ? 'rounded-md bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/30 dark:text-red-300'
              : 'rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
          }
        >
          <span className="block font-medium">
            {issue.title}
            {issue.level === 'warning' && ' · предупреждение'}
          </span>
          <span className="mt-0.5 block break-words">{issue.text}</span>
        </div>
      ))}
    </div>
  )
}

export interface OverviewTabProps {
  model: McardModel
  presenceEntries: ProductPresenceEntry[] | null
  currentSellerId: number | null
  canOpenOther: boolean
  onOpenOther: (target: EntityTarget) => void
  /** Площадочные блоки: претензии ВБ, публикация и SEO-канал. */
  extra?: React.ReactNode
  /** Связанные сущности (бывшая вкладка «Связи»). */
  relations?: React.ReactNode
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  model,
  presenceEntries,
  currentSellerId,
  canOpenOther,
  onOpenOther,
  extra,
  relations,
}) => {
  const hero = model.photos[0]
  const old = Number(model.price.old)
  const showOld = Number.isFinite(old) && old > 0 && model.price.shown !== null && old > Number(model.price.shown)
  return (
    <div className="space-y-4">
      <Panel title="Как видит покупатель">
        <div className="flex gap-4">
          <div className="w-32 shrink-0">
            {hero ? (
              <img
                src={hero}
                alt={model.title || model.article}
                className="aspect-[3/4] w-full rounded-md border border-gray-200 object-contain dark:border-zinc-800"
              />
            ) : (
              <div className="flex aspect-[3/4] w-full items-center justify-center rounded-md border border-dashed border-gray-300 text-gray-300 dark:border-zinc-700 dark:text-zinc-600">
                <ImageOff className="h-6 w-6" aria-hidden="true" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <div className="text-base font-semibold text-gray-900 dark:text-zinc-100">
                {model.title || <NoData why="название не пришло с площадки" />}
              </div>
              <div className="mt-1 flex flex-wrap items-baseline gap-2">
                <span className="text-lg font-semibold tabular-nums text-gray-900 dark:text-zinc-100">
                  {model.price.shown !== null ? formatRub(model.price.shown) : <NoData why="площадка не прислала цену" />}
                </span>
                {showOld && (
                  <span className="text-xs text-gray-400 line-through dark:text-zinc-500">{formatRub(old)}</span>
                )}
                {Number(model.price.discountPercent) > 0 && (
                  <span className="text-xs text-gray-500 dark:text-zinc-400">скидка {model.price.discountPercent} %</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <ReadonlyValue label="Артикул продавца" value={model.article} mono />
              <ReadonlyValue label="Бренд" value={model.brand} />
              <ReadonlyValue label="Категория" value={model.categoryName} />
              <ReadonlyValue label="Фото" value={`${model.photos.length}`} />
              <ReadonlyValue
                label="Остаток"
                value={model.stock !== null ? `${model.stock.toLocaleString('ru-RU')} шт.` : null}
                why={model.stockWhy}
              />
              <ReadonlyValue label="Статус" value={model.statusLabel} />
            </div>
          </div>
        </div>
      </Panel>

      <Panel title="Ошибки и модерация площадки">
        <IssuesBlock issues={model.issues} />
      </Panel>

      {extra}

      <Panel title="Та же карточка на других площадках">
        {presenceEntries === null ? (
          <Notice>Присутствие на площадках ещё загружается или недоступно.</Notice>
        ) : presenceEntries.length === 0 ? (
          <Notice>У артикула нет карточек на площадках.</Notice>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {presenceEntries.map((entry) => {
              const isCurrent =
                entry.marketplace === model.platform &&
                (currentSellerId === null || Number(entry.seller_id) === Number(currentSellerId))
              const target = presenceTarget(entry, model.article)
              const info = PRESENCE_STATE_INFO[entry.state]
              const cabinet = entry.cabinet_code || entry.seller_short_name || entry.seller_name || '—'
              return (
                <li key={`${entry.marketplace}:${entry.seller_id ?? ''}`} className="flex items-center gap-3 py-2 text-sm">
                  <span className="w-12 shrink-0 font-medium text-gray-800 dark:text-zinc-200">
                    {PRESENCE_CHAIN_LABEL[entry.marketplace]}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-gray-600 dark:text-zinc-300">{cabinet}</span>
                  <Badge variant={info?.variant ?? 'gray'}>{info?.label ?? entry.state}</Badge>
                  {isCurrent ? (
                    <span className="w-24 shrink-0 text-right text-xs text-gray-400 dark:text-zinc-500">это окно</span>
                  ) : target && canOpenOther ? (
                    <button
                      type="button"
                      onClick={() => onOpenOther(target)}
                      className="w-24 shrink-0 text-right text-xs text-indigo-700 hover:underline dark:text-indigo-400"
                    >
                      Открыть
                    </button>
                  ) : (
                    <span className="w-24 shrink-0 text-right text-xs text-gray-400 dark:text-zinc-500">нет карточки</span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </Panel>

      {relations}
    </div>
  )
}

// ── Текст ───────────────────────────────────────────────────────────────────

export type TextMode = 'showcase' | 'pim' | 'diff'

const FieldPair: React.FC<{
  label: string
  showcase: string
  pim: string | null
  mode: TextMode
  multiline?: boolean
}> = ({ label, showcase, pim, mode, multiline }) => {
  const same = pim === null || showcase.trim() === pim.trim()
  const text = (value: string | null) =>
    value && value.trim() ? (
      <span className={cn('whitespace-pre-wrap break-words', multiline ? 'text-sm' : 'text-sm font-medium')}>{value}</span>
    ) : (
      <NoData />
    )
  if (mode === 'diff') {
    return (
      <div>
        <div className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-zinc-300">
          {label}
          {pim !== null && (
            <Badge variant={same ? 'green' : 'yellow'}>{same ? 'Совпадает' : 'Расходится'}</Badge>
          )}
        </div>
        <div className={cn('grid gap-3', same ? 'sm:grid-cols-1' : 'sm:grid-cols-2')}>
          <div className="rounded-md border border-gray-200 p-3 dark:border-zinc-800">
            <div className="mb-1 text-[11px] text-gray-500 dark:text-zinc-500">Витрина</div>
            {text(showcase)}
          </div>
          {!same && (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/20">
              <div className="mb-1 text-[11px] text-gray-500 dark:text-zinc-500">ПИМ</div>
              {text(pim)}
            </div>
          )}
        </div>
      </div>
    )
  }
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-gray-700 dark:text-zinc-300">{label}</div>
      <div className="rounded-md border border-gray-200 p-3 dark:border-zinc-800">
        {text(mode === 'pim' ? pim : showcase)}
      </div>
    </div>
  )
}

export interface TextTabProps {
  model: McardModel
  state: CardWindowState | null
  edit: McardEditApi
  mode: TextMode
  onMode: (mode: TextMode) => void
  /** Почему правка недоступна (показывается над полями); null — доступна. */
  readOnlyReason: string | null
  /** Дополнительные блоки внизу (у Сайта — собранная запись). */
  footer?: React.ReactNode
}

export const TextTab: React.FC<TextTabProps> = ({ model, state, edit, mode, onMode, readOnlyReason, footer }) => {
  const pimTitle = state ? state.fields.title.value : null
  const pimDescription = state ? state.fields.description.value : null
  return (
    <div className="space-y-4">
      <Panel
        title="Наименование и описание"
        aside={
          !edit.editing ? (
            <Segmented<TextMode>
              ariaLabel="Что показывать"
              value={state ? mode : 'showcase'}
              onChange={onMode}
              options={[
                { key: 'showcase', label: 'Витрина' },
                { key: 'pim', label: 'ПИМ', disabled: !state, title: state ? undefined : 'нет данных: карточки нет в ПИМ' },
                { key: 'diff', label: 'Разница', disabled: !state, title: state ? undefined : 'нет данных: карточки нет в ПИМ' },
              ]}
            />
          ) : (
            <Badge variant="yellow">Режим правки</Badge>
          )
        }
      >
        {edit.editing ? (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-gray-700 dark:text-zinc-300">
                Наименование <span className="text-red-500">*</span>
              </span>
              <input
                value={edit.draft.title}
                onChange={(event) => edit.setTitle(event.target.value)}
                className={fieldClass(!edit.draft.title.trim())}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700 dark:text-zinc-300">
                Описание <span className="text-red-500">*</span>
              </span>
              <textarea
                rows={8}
                value={edit.draft.description}
                onChange={(event) => edit.setDescription(event.target.value)}
                className={fieldClass(!edit.draft.description.trim())}
              />
            </label>
            <Notice>
              Правится значение в ПИМ. На площадку оно уйдёт через журнал правок после подтверждения.
            </Notice>
          </div>
        ) : (
          <div className="space-y-4">
            <FieldPair label="Наименование" showcase={model.title} pim={pimTitle} mode={state ? mode : 'showcase'} />
            <FieldPair label="Описание" showcase={model.description} pim={pimDescription} mode={state ? mode : 'showcase'} multiline />
            {!state && (
              <Notice>
                Сравнения с ПИМ нет: {readOnlyReason ?? 'карточки нет в ПИМ'}. Показано то, что на витрине.
              </Notice>
            )}
          </div>
        )}
      </Panel>

      <RequisitesPanel model={model} state={state} edit={edit} />

      {footer}
    </div>
  )
}

// ── Бренд, габариты, штрихкод ───────────────────────────────────────────────

/** Подсказка о причине закрытого поля: серым мелким текстом под значением. */
const LockedNote: React.FC<{ reason: string | null }> = ({ reason }) =>
  reason ? <div className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-500">{reason}</div> : null

const requisiteLabelClass = 'text-xs font-medium text-gray-700 dark:text-zinc-300'

const DIMENSION_UNITS: Record<DimensionAxis, string> = { length: 'см', width: 'см', height: 'см', weight: 'кг' }

const RequisitesPanel: React.FC<{ model: McardModel; state: CardWindowState | null; edit: McardEditApi }> = ({
  model,
  state,
  edit,
}) => {
  const brandField = state?.fields.brand
  const dimensionsField = state?.fields.dimensions
  const barcodeField = state?.fields.barcode
  const platformName = PLATFORM_NAME[model.platform]

  const brandEditable = edit.editing && brandField?.editable === true
  const dimensionsEditable = edit.editing && dimensionsField?.editable === true

  // Чтение: значение ПИМ, если оно отличается от витрины; иначе то, что на витрине.
  const brandText = brandField?.overridden ? brandField.value : (model.brand ?? brandField?.value ?? null)
  const dimensionsText = dimensionsField?.overridden
    ? formatDimensions(dimensionsField.value)
    : (model.dimensionsText ?? formatDimensions(dimensionsField?.value))
  const barcodeText = barcodeField?.overridden ? barcodeField.value : (model.barcode ?? barcodeField?.value ?? null)
  const platformDimensions = dimensionsField?.overridden ? formatDimensions(dimensionsField.platform_value) : null

  return (
    <Panel
      title="Бренд, габариты, штрихкод"
      aside={
        edit.editing ? undefined : (
          <span className="text-[11px] text-gray-500 dark:text-zinc-500">
            Правятся в режиме правки, где площадка это позволяет
          </span>
        )
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          {brandEditable ? (
            <label className="block">
              <span className={requisiteLabelClass}>Бренд</span>
              <input
                aria-label="Бренд"
                value={edit.draft.brand}
                onChange={(event) => edit.setBrand(event.target.value)}
                className={fieldClass(!edit.draft.brand.trim())}
              />
            </label>
          ) : (
            <>
              <ReadonlyValue label="Бренд" value={brandText} />
              {brandField?.overridden && brandField.platform_value && (
                <div className="mt-0.5 text-[10px] text-amber-700 dark:text-amber-400">
                  Правка в ПИМ, на витрине: {brandField.platform_value}
                </div>
              )}
              {brandField && !brandField.editable && <LockedNote reason={brandField.locked_reason} />}
            </>
          )}
        </div>

        <div className="min-w-0">
          <ReadonlyValue
            label="Артикул продавца"
            value={model.article}
            mono
            hint="По артикулу карточка связана с ПИМ, поэтому он не правится"
          />
        </div>

        <div className="min-w-0 sm:col-span-2">
          {dimensionsEditable ? (
            <fieldset>
              <legend className={requisiteLabelClass}>Габариты</legend>
              <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {DIMENSION_AXES.map((axis) => (
                  <label key={axis} className="block">
                    <span className="text-[11px] text-gray-500 dark:text-zinc-500">
                      {DIMENSION_AXIS_LABELS[axis]}, {DIMENSION_UNITS[axis]}
                    </span>
                    <input
                      aria-label={`${DIMENSION_AXIS_LABELS[axis]}, ${DIMENSION_UNITS[axis]}`}
                      inputMode="decimal"
                      value={edit.draft.dimensions[axis]}
                      onChange={(event) => edit.setDimension(axis, event.target.value)}
                      className={cn(fieldClass(!edit.draft.dimensions[axis].trim()), 'mt-0.5 tabular-nums')}
                    />
                  </label>
                ))}
              </div>
              <p className="mt-1 text-[10px] text-gray-400 dark:text-zinc-500">
                Длина, ширина, высота — целые сантиметры; вес — килограммы (запятая допустима). Можно менять часть полей.
              </p>
            </fieldset>
          ) : (
            <>
              <ReadonlyValue label="Габариты" value={dimensionsText} why={`${platformName} не прислала габариты`} />
              {platformDimensions && (
                <div className="mt-0.5 text-[10px] text-amber-700 dark:text-amber-400">
                  Правка в ПИМ, на витрине: {platformDimensions}
                </div>
              )}
              {dimensionsField && !dimensionsField.editable && <LockedNote reason={dimensionsField.locked_reason} />}
            </>
          )}
        </div>

        {/* Штрихкод не правится ни на одной площадке (ОЗОН выдаёт его сама): только чтение с причиной. */}
        <div className="min-w-0 sm:col-span-2">
          <ReadonlyValue label="Штрихкод" value={barcodeText} mono />
          {barcodeField && !barcodeField.editable && <LockedNote reason={barcodeField.locked_reason} />}
        </div>
      </div>
    </Panel>
  )
}

// ── Характеристики ──────────────────────────────────────────────────────────

export interface CharacteristicsTabProps {
  rows: CharacteristicRow[]
  state: CardWindowState | null
  edit: McardEditApi
  specsKnown: boolean
  platformName: string
  search: string
  onSearch: (value: string) => void
  onlyMissing: boolean
  onOnlyMissing: (value: boolean) => void
}

export const CharacteristicsTab: React.FC<CharacteristicsTabProps> = ({
  rows,
  state,
  edit,
  specsKnown,
  platformName,
  search,
  onSearch,
  onlyMissing,
  onOnlyMissing,
}) => {
  const query = search.trim().toLowerCase()
  const draftValue = (row: CharacteristicRow): string | undefined =>
    row.targetId !== null ? edit.draft.characteristics[String(row.targetId)] : undefined
  const shown = rows.filter((row) => {
    if (query && !row.name.toLowerCase().includes(query)) return false
    if (onlyMissing) {
      const value = edit.editing ? (draftValue(row) ?? effectiveValue(row)) : row.platformValue
      if (!(row.required && !value.trim())) return false
    }
    return true
  })
  const showOrigin = Boolean(state)

  return (
    <Panel title={`Характеристики ${platformName}`}>
      {!specsKnown && (
        <div className="mb-3">
          <Notice tone="warn">
            Справочник характеристик предмета не загружен: обязательность полей неизвестна.
          </Notice>
        </div>
      )}
      <div className="mb-4 flex flex-wrap gap-2">
        <label className="relative min-w-56 flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Поиск по названию"
            aria-label="Поиск по названию характеристики"
            className={cn(fieldClass(), 'mt-0 pl-9')}
          />
        </label>
        <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 text-xs dark:border-zinc-700">
          <input type="checkbox" checked={onlyMissing} onChange={(event) => onOnlyMissing(event.target.checked)} />
          Только незаполненные обязательные
        </label>
      </div>

      {rows.length === 0 ? (
        <Notice>Площадка не прислала характеристик по этой карточке.</Notice>
      ) : shown.length === 0 ? (
        <Notice>Под фильтр ничего не попало.</Notice>
      ) : (
        <div className="overflow-hidden rounded-md border border-gray-200 dark:border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:bg-zinc-950 dark:text-zinc-400">
              <tr>
                <th className="px-3 py-2 font-medium">Характеристика</th>
                <th className="px-3 py-2 font-medium">Значение</th>
                {showOrigin && <th className="w-36 px-3 py-2 font-medium">Откуда</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
              {shown.map((row) => {
                const draft = draftValue(row)
                const current = draft ?? effectiveValue(row)
                const invalid = row.required && !current.trim()
                const differs = row.pimValue !== null && row.pimValue.trim() !== row.platformValue.trim()
                return (
                  <tr key={row.key} className="align-top">
                    <td className="px-3 py-2">
                      <div className="text-xs font-medium text-gray-800 dark:text-zinc-200">
                        {row.name}
                        {row.required && <span className="text-red-500"> *</span>}
                      </div>
                      {row.hint && <div className="mt-0.5 font-mono text-[10px] text-gray-400 dark:text-zinc-500">{row.hint}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {edit.editing ? (
                        row.editable && row.targetId !== null ? (
                          <input
                            aria-label={row.name}
                            value={current}
                            onChange={(event) => edit.setCharacteristic(row.targetId as number, event.target.value)}
                            className={cn(fieldClass(invalid), 'mt-0')}
                          />
                        ) : (
                          <div title={row.readOnlyReason ?? undefined}>
                            {current.trim() ? (
                              <span className="break-words text-sm text-gray-700 dark:text-zinc-300">{current}</span>
                            ) : (
                              <NoData why={row.readOnlyReason ?? undefined} />
                            )}
                            {row.readOnlyReason && (
                              <div className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-500">
                                только чтение: {row.readOnlyReason}
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <div>
                          {row.platformValue.trim() ? (
                            <span className="break-words text-sm text-gray-800 dark:text-zinc-200">{row.platformValue}</span>
                          ) : invalid ? (
                            <span className="text-sm text-red-600 dark:text-red-400">не заполнено</span>
                          ) : (
                            <NoData />
                          )}
                          {differs && (
                            <div className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                              ПИМ: {row.pimValue}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    {showOrigin && (
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-zinc-400">
                        {row.overridden ? (
                          <Badge variant="yellow">{ORIGIN_LABELS.pim_override}</Badge>
                        ) : (
                          ORIGIN_LABELS[row.origin]
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {edit.editing && (
        <p className="mt-3 text-[11px] text-gray-500 dark:text-zinc-400">
          Добавить новую характеристику и очистить значение из окна нельзя: пустое поле не отправляется.
        </p>
      )}
    </Panel>
  )
}

// ── Цена и остатки (только просмотр) ────────────────────────────────────────

export interface PriceTabProps {
  platform: McardPlatform
  children: React.ReactNode
}

const PRICING_LINK: Partial<Record<McardPlatform, { href: string; note?: string }>> = {
  wb: { href: '/pricing/wb' },
  ozon: { href: '/pricing/ozon', note: 'Раздел цен Ozon пока в разработке.' },
}

export const PriceTab: React.FC<PriceTabProps> = ({ platform, children }) => {
  const link = PRICING_LINK[platform]
  return (
    <div className="space-y-4">
      <Notice>
        Цена и остатки показаны только для просмотра. Цена меняется в разделе «Цены».{' '}
        {link ? (
          <>
            <a href={link.href} className="font-medium text-indigo-700 hover:underline dark:text-indigo-400">
              Изменить в разделе «Цены»
            </a>
            {link.note ? ` ${link.note}` : ''}
          </>
        ) : (
          <span>Для {PLATFORM_NAME[platform]} отдельного раздела цен пока нет.</span>
        )}
      </Notice>
      {children}
    </div>
  )
}

// ── Склейка ─────────────────────────────────────────────────────────────────

const BUNDLE_STATE_LABELS: Record<string, string> = {
  applied: 'Применена на площадке',
  added: 'Добавляется в склейку',
  moved_in: 'Переезжает в эту склейку',
  leaving: 'Уходит из склейки',
}

const BUNDLES_SECTION: Partial<Record<McardPlatform, string>> = {
  wb: '/wildberries',
  ozon: '/ozon',
  ym: '/yandex-market',
}

export interface BundleTabProps {
  platform: McardPlatform
  sellerId: number | null
  article: string
  /** Номер склейки на площадке из детали (ВБ imtID, Маркет группа). */
  externalId?: string | number | null
  externalLabel?: string
  active: boolean
}

export const BundleTab: React.FC<BundleTabProps> = ({ platform, sellerId, article, externalId, externalLabel, active }) => {
  const [card, setCard] = useState<MarketplaceBundleCard | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const supported = platform !== 'site' && sellerId !== null

  useEffect(() => {
    if (!active || !supported || !article) return undefined
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    marketplaceBundlesAPI
      .cards({ marketplace: platform, seller_id: sellerId, articles: article, limit: 1, offset: 0 }, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return
        setCard(page.items.find((item) => item.article === article) ?? page.items[0] ?? null)
      })
      .catch((requestError: any) => {
        if (controller.signal.aborted || requestError?.code === 'ERR_CANCELED') return
        setError(requestError?.response?.data?.error?.message || requestError?.message || 'Не удалось прочитать склейку')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [active, article, platform, sellerId, supported])

  const section = BUNDLES_SECTION[platform]
  return (
    <div className="space-y-4">
      <Panel title="Склейка карточки">
        {!supported ? (
          <Notice>У карточки сайта склеек нет.</Notice>
        ) : loading ? (
          <Notice>Читаем склейку…</Notice>
        ) : error ? (
          <Notice tone="bad">{error}</Notice>
        ) : card ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <ReadonlyValue label="Склейка" value={card.bundle.name} />
            <ReadonlyValue label="Состояние" value={BUNDLE_STATE_LABELS[card.state] ?? card.state} />
            <ReadonlyValue
              label="Товаров в склейке"
              value={`${card.bundle.size} (на площадке ${card.bundle.applied_size} из ${card.bundle.capacity})`}
            />
            <ReadonlyValue
              label="Номер склейки на площадке"
              value={card.bundle.external_bundle_id ?? (externalId != null ? String(externalId) : null)}
              mono
              why="склейка ещё не применена на площадке"
            />
            <ReadonlyValue
              label="Неотправленные правки"
              value={card.bundle.pending_changes > 0 ? String(card.bundle.pending_changes) : 'нет'}
            />
            {card.bundle.last_error && <ReadonlyValue label="Ошибка площадки" value={card.bundle.last_error} />}
          </div>
        ) : (
          <div className="space-y-3">
            <Notice>Карточка не входит ни в одну склейку ПИМ.</Notice>
            {externalId != null && externalId !== '' && (
              <ReadonlyValue label={externalLabel ?? 'Номер склейки на площадке'} value={String(externalId)} mono />
            )}
          </div>
        )}
        {section && (
          <p className="mt-4 text-xs text-gray-600 dark:text-zinc-300">
            <a href={section} className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:underline dark:text-indigo-400">
              <Layers className="h-3.5 w-3.5" aria-hidden="true" />
              Открыть раздел склеек
            </a>{' '}
            (вкладка «Склейки» каталога). Состав склейки из окна не меняется.
          </p>
        )}
      </Panel>
    </div>
  )
}

// ── История ─────────────────────────────────────────────────────────────────

export interface HistoryTabProps {
  platform: McardPlatform
  sellerId: number | null
  article: string
  state: CardWindowState | null
  lastSyncAt: string | null
  active: boolean
  /** Источник правок — разворачивается рядом (у Сайта — «История обмена»). */
  extra?: React.ReactNode
  /** Увеличивается после правки: история перечитывается. */
  reloadKey: number
}

const journalValue = (value: unknown): string => {
  if (value === null || value === undefined) return NO_DATA_SHORT
  if (typeof value === 'object') {
    try {
      const entries = Object.entries(value as Record<string, unknown>)
      if (entries.length === 0) return NO_DATA_SHORT
      return entries
        // Габариты — «длина 40 см, вес 0,15 кг», а не JSON оси-значение.
        .map(([key, item]) => `${key}: ${key === 'dimensions' ? formatPlainValue(item) : characteristicText(item)}`)
        .join(' · ')
        .slice(0, 300)
    } catch {
      return NO_DATA_SHORT
    }
  }
  return characteristicText(value).slice(0, 300) || NO_DATA_SHORT
}
const NO_DATA_SHORT = 'нет данных'

export const HistoryTab: React.FC<HistoryTabProps> = ({
  platform,
  sellerId,
  article,
  state,
  lastSyncAt,
  active,
  extra,
  reloadKey,
}) => {
  const [sends, setSends] = useState<CardChangeSendSummary[] | null>(null)
  const [changes, setChanges] = useState<ChangeSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const supported = platform !== 'site' && sellerId !== null

  useEffect(() => {
    if (!active || !supported || !article) return undefined
    const controller = new AbortController()
    setError(null)
    const base = {
      marketplace: platform as CardChangeMarketplace,
      seller_id: sellerId as number,
      article,
    }
    Promise.allSettled([
      cardChangeSendsAPI.list({ ...base, limit: 20, offset: 0 }, controller.signal),
      marketplaceCardChangesAPI.list(
        { ...base, status: ['detected', 'approved', 'sending', 'sent', 'failed', 'discarded', 'stale'], limit: 20, offset: 0 },
        controller.signal,
      ),
    ]).then(([sendsResult, changesResult]) => {
      if (controller.signal.aborted) return
      setSends(sendsResult.status === 'fulfilled' ? sendsResult.value.items : [])
      setChanges(changesResult.status === 'fulfilled' ? changesResult.value.items : [])
      if (sendsResult.status === 'rejected' && changesResult.status === 'rejected') {
        setError('Не удалось прочитать журнал отправок и наборов изменений')
      }
    })
    return () => controller.abort()
  }, [active, article, platform, sellerId, supported, reloadKey])

  const journal = state?.history.journal ?? []
  return (
    <div className="space-y-4">
      <Panel title="Сверка">
        <div className="grid gap-4 sm:grid-cols-3">
          <ReadonlyValue label="Сверена с площадкой" value={lastSyncAt ? formatDateTime(lastSyncAt) : null} why="сверка ещё не выполнялась" />
          <ReadonlyValue
            label="Копия карточки в ПИМ обновлена"
            value={state?.mirror_synced_at ? formatDateTime(state.mirror_synced_at) : null}
            why={state ? 'копии карточки нет' : 'карточки нет в ПИМ'}
          />
          <ReadonlyValue label="Ревизия карточки в ПИМ" value={state?.revision ? `#${state.revision}` : null} mono why="правок из окна не было" />
        </div>
      </Panel>

      {extra}

      {supported && (
        <>
          <Panel title="Правки из окна">
            {!state ? (
              <Notice>Нет данных: карточки нет в ПИМ, правок из окна не было.</Notice>
            ) : journal.length === 0 ? (
              <Notice>Правок из окна не было.</Notice>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                {journal.map((record) => (
                  <li key={record.id} className="space-y-1 py-2 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-800 dark:text-zinc-200">
                        {record.field_labels.length ? record.field_labels.join(', ') : 'Правка'}
                      </span>
                      <Badge variant={record.status === 'completed' ? 'green' : record.status === 'dismissed' ? 'gray' : 'blue'}>
                        {record.status}
                      </Badge>
                      <span className="ml-auto tabular-nums text-gray-500 dark:text-zinc-400">{formatDateTime(record.changed_at)}</span>
                    </div>
                    <div className="text-gray-500 dark:text-zinc-400">
                      {record.changed_by_name ?? (record.changed_by ? `Пользователь #${record.changed_by}` : 'Автор неизвестен')}
                    </div>
                    <div className="grid gap-1 sm:grid-cols-2">
                      <div className="break-words rounded bg-gray-50 px-2 py-1 text-gray-600 dark:bg-zinc-950 dark:text-zinc-400">
                        Было: {journalValue(record.value_before)}
                      </div>
                      <div className="break-words rounded bg-gray-50 px-2 py-1 text-gray-800 dark:bg-zinc-950 dark:text-zinc-200">
                        Стало: {journalValue(record.value_after)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Отправки на площадку">
            {sends === null ? (
              <Notice>Читаем журнал отправок…</Notice>
            ) : sends.length === 0 ? (
              <Notice>Отправок по этой карточке не было.</Notice>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                {sends.map((send) => {
                  const outcome = SEND_OUTCOME_LABELS[send.outcome]
                  return (
                    <li key={send.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-xs">
                      <Badge variant={outcome?.variant ?? 'gray'}>{outcome?.label ?? send.outcome}</Badge>
                      <span className="text-gray-700 dark:text-zinc-300">{send.summary || 'Отправка'}</span>
                      <span className="text-gray-500 dark:text-zinc-400">{sendOriginLabel(send.send_origin)}</span>
                      {send.error && <span className="min-w-0 max-w-full break-words text-red-700 dark:text-red-300">{send.error}</span>}
                      <span className="ml-auto tabular-nums text-gray-500 dark:text-zinc-400">{formatDateTime(send.created_at)}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </Panel>

          <Panel title="Наборы изменений">
            {changes === null ? (
              <Notice>Читаем наборы изменений…</Notice>
            ) : changes.length === 0 ? (
              <Notice>Наборов изменений по этой карточке нет.</Notice>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                {changes.map((change) => {
                  const status = CHANGE_STATUS_LABELS[change.status]
                  return (
                    <li key={change.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-xs">
                      <Badge variant={status?.variant ?? 'gray'}>{status?.label ?? change.status}</Badge>
                      <span className="text-gray-700 dark:text-zinc-300">Позиций: {change.items_count}</span>
                      {change.error && <span className="min-w-0 break-words text-red-700 dark:text-red-300">{change.error}</span>}
                      <span className="ml-auto tabular-nums text-gray-500 dark:text-zinc-400">{formatDateTime(change.detected_at)}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </Panel>
          {error && <Notice tone="bad">{error}</Notice>}
        </>
      )}
    </div>
  )
}

// ── Прочее ──────────────────────────────────────────────────────────────────

/** Плашка «правка недоступна»: причина словами и, если есть, действие. */
export const ReadOnlyBanner: React.FC<{ reason: string; action?: React.ReactNode }> = ({ reason, action }) => (
  <div className="flex shrink-0 items-start gap-2.5 border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    <div className="min-w-0 flex-1">
      <span className="font-medium">Только чтение.</span> {reason}
    </div>
    {action}
  </div>
)

export const ExternalAnchor: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:underline dark:text-indigo-400"
  >
    {children}
    <ExternalLink className="h-3 w-3" aria-hidden="true" />
  </a>
)
